import { isProduction } from "@config/site/helpers";
import type { StoreData, WalkInHours } from "./controller";
import { dayOfWeek } from "./controller";
import { LogTag, Logger, ServiceType } from "@lib/monitoring/logger";
import type { Store } from "./fetch-store";

export const formatStreet = (storeData: StoreData) => {
    return storeData?.street2 ? `${storeData?.street1}, ${storeData.street2}` : storeData?.street1;
};

const reduceHour = (hour?: string) => hour?.split(":").slice(0, 2).join(":") || "";

export const normalizeStore = (stores: Store[], locale: string) => {
    return stores
        .filter((store) => {
            const { custom } = store;
            const {
                store_CF_Latitude,
                store_CF_Longitude,
                store_CF_Timezone,
                store_CF_Deleted,
                store_CF_StoreLocator,
                store_CF_OpeningDate,
                store_CF_ClosingDate,
            } = custom?.fields || {};

            if (!store.custom && !isProduction) {
                Logger.info(ServiceType.WEB, "Store Data Error: No custom field", {
                    tag: LogTag.STORES,
                    storeKey: store.key,
                });
                return false;
            }

            if ((!store_CF_Latitude || !store_CF_Longitude) && !isProduction) {
                Logger.error(ServiceType.WEB, "Store Data Error: Invalid Lat/Lng", {
                    tag: LogTag.STORES,
                    storeKey: store.key,
                });
                return false;
            }

            if (!store_CF_Timezone && !isProduction) {
                Logger.error(ServiceType.WEB, "Store Data Error: Invalid TimeZone", {
                    tag: LogTag.STORES,
                    storeKey: store.key,
                });
                return false;
            }

            return !(
                store_CF_Deleted ||
                !store_CF_StoreLocator ||
                new Date(store_CF_OpeningDate) > new Date() ||
                new Date(store_CF_ClosingDate) < new Date()
            );
        })
        .map((store) => {
            const { custom } = store;
            if (custom && !custom.fields.store_CF_Deleted) {
                const {
                    store_CF_City,
                    store_CF_Country,
                    store_CF_Email,
                    store_CF_Latitude,
                    store_CF_Longitude,
                    store_CF_Name1,
                    store_CF_OperatingHours,
                    store_CF_Ownership,
                    store_CF_PostalCode,
                    store_CF_Region,
                    store_CF_ProductMix,
                    store_CF_Street1,
                    store_CF_Street2,
                    store_CF_Telephone,
                    store_CF_Timezone,
                    store_CF_PickupInStore,
                } = custom.fields;

                const walkInHours: WalkInHours = store_CF_OperatingHours?.obj?.value || {};
                const walkInHoursNormalized = dayOfWeek.reduce((days, day) => {
                    // eslint-disable-next-line no-param-reassign
                    days[day] = {
                        open: reduceHour(walkInHours[day]?.open),
                        close: reduceHour(walkInHours[day]?.close),
                    };
                    return days;
                }, {} as WalkInHours);

                return {
                    storeId: store.key,
                    name: store_CF_Name1?.[locale] || store_CF_Name1?.en || "",
                    street1: store_CF_Street1?.[locale] || store_CF_Street1?.en || "",
                    street2: store_CF_Street2?.[locale] || store_CF_Street2?.en || "",
                    city: store_CF_City?.[locale] || store_CF_City?.en || "",
                    postalCode: store_CF_PostalCode || "",
                    region: store_CF_Region || "",
                    phone: store_CF_Telephone || "",
                    countryCode: store_CF_Country || "",
                    email: store_CF_Email || "",
                    storeTypeId: store_CF_Ownership || "",
                    categories: store_CF_ProductMix?.obj?.value || [],
                    latitude: store_CF_Latitude || 0,
                    longitude: store_CF_Longitude || 0,
                    timeZone: store_CF_Timezone || "",
                    walkInHours: walkInHoursNormalized,
                    distributionChannels: store.distributionChannels,
                    supplyChannel: store.supplyChannels,
                    pickupInStore: store_CF_PickupInStore || false,
                };
            }
        }) as StoreData[];
};
